import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import { AuthProvider } from "./hooks/useAuth";

// Lazy load ERP wrapper to reduce initial bundle (includes sidebar, all ERP pages)
const ERPWrapper = lazy(() => import('./components/ERPWrapper'));

// Lazy load PWA components - not critical for initial render
const PWAInstallPrompt = lazy(() => import('./components/PWAInstallPrompt').then(m => ({ default: m.PWAInstallPrompt })));
const PWAUpdatePrompt = lazy(() => import('./components/PWAUpdatePrompt').then(m => ({ default: m.PWAUpdatePrompt })));

// Lazy load non-critical pages
const About = lazy(() => import("./pages/About"));
const Projects = lazy(() => import("./pages/Projects"));
const GPRExotica = lazy(() => import("./pages/GPRExotica"));
const GPRKuberaKshetram = lazy(() => import("./pages/GPRKuberaKshetram"));
const SreeSaiNagar = lazy(() => import("./pages/SreeSaiNagar"));
const SreeAmbikaNagar = lazy(() => import("./pages/SreeAmbikaNagar"));
const EasternMeadows = lazy(() => import("./pages/EasternMeadows"));
const SmartCityAmangal = lazy(() => import("./pages/SmartCityAmangal"));
const AgentPartner = lazy(() => import("./pages/AgentPartner"));
const Contact = lazy(() => import("./pages/Contact"));
const ERPLanding = lazy(() => import("./pages/ERPLanding"));
const Auth = lazy(() => import("./pages/Auth"));
const MetaAdsLanding = lazy(() => import("./pages/MetaAdsLanding"));
const PlotsNearORR = lazy(() => import("./pages/PlotsNearORR"));
const PlotsNearRRR = lazy(() => import("./pages/PlotsNearRRR"));
const OpenPlotsEastHyderabad = lazy(() => import("./pages/OpenPlotsEastHyderabad"));
const OpenPlotsHyderabad = lazy(() => import("./pages/OpenPlotsHyderabad"));
const FarmlandHyderabad = lazy(() => import("./pages/FarmlandHyderabad"));
const PlotsInLocation = lazy(() => import("./pages/PlotsInLocation"));
const AllLocations = lazy(() => import("./pages/AllLocations"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Agent Mobile App
const AgentDashboard = lazy(() => import("./pages/agent-app/AgentDashboard"));
const AgentPlots = lazy(() => import("./pages/agent-app/AgentPlots"));
const AgentProfile = lazy(() => import("./pages/agent-app/AgentProfile"));
const AgentAuth = lazy(() => import("./pages/agent-app/AgentAuth"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <PWAUpdatePrompt />
        <PWAInstallPrompt />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Main website routes with Navbar and Footer */}
              <Route path="/" element={<><Navbar /><Home /><Footer /></>} />
              <Route path="/about" element={<><Navbar /><About /><Footer /></>} />
              <Route path="/projects" element={<><Navbar /><Projects /><Footer /></>} />
              <Route path="/projects/gpr-exotica" element={<><Navbar /><GPRExotica /><Footer /></>} />
              <Route path="/projects/gpr-kubera-kshetram" element={<><Navbar /><GPRKuberaKshetram /><Footer /></>} />
              <Route path="/projects/sree-sai-nagar" element={<><Navbar /><SreeSaiNagar /><Footer /></>} />
              <Route path="/projects/sree-ambika-nagar" element={<><Navbar /><SreeAmbikaNagar /><Footer /></>} />
              <Route path="/projects/eastern-meadows" element={<><Navbar /><EasternMeadows /><Footer /></>} />
              <Route path="/projects/smart-city-amangal" element={<><Navbar /><SmartCityAmangal /><Footer /></>} />
              <Route path="/erp" element={<><Navbar /><ERPLanding /><Footer /></>} />
              <Route path="/agent-partner" element={<><Navbar /><AgentPartner /><Footer /></>} />
              <Route path="/contact" element={<><Navbar /><Contact /><Footer /></>} />
              <Route path="/plots-near-orr-hyderabad" element={<><Navbar /><PlotsNearORR /><Footer /></>} />
              <Route path="/plots-near-rrr-hyderabad" element={<><Navbar /><PlotsNearRRR /><Footer /></>} />
              <Route path="/open-plots-east-hyderabad" element={<><Navbar /><OpenPlotsEastHyderabad /><Footer /></>} />
              <Route path="/open-plots-hyderabad" element={<><Navbar /><OpenPlotsHyderabad /><Footer /></>} />
              <Route path="/farmland-hyderabad" element={<><Navbar /><FarmlandHyderabad /><Footer /></>} />
              <Route path="/all-locations" element={<AllLocations />} />
              <Route path="/plots-in-:slug" element={<PlotsInLocation />} />
              <Route path="/blog" element={<><Navbar /><Blog /><Footer /></>} />
              <Route path="/blog/:slug" element={<><Navbar /><BlogPost /><Footer /></>} />
              
              {/* Meta Ads Landing Page - No navbar/footer for clean conversion */}
              <Route path="/lp" element={<MetaAdsLanding />} />
              
              {/* ERP app routes without Navbar/Footer */}
              <Route path="/erp/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/erp/*" element={<ERPWrapper />} />
              
              {/* Agent Mobile App routes */}
              <Route path="/agent" element={<AgentDashboard />} />
              <Route path="/agent/plots" element={<AgentPlots />} />
              <Route path="/agent/profile" element={<AgentProfile />} />
              <Route path="/agent/auth" element={<AgentAuth />} />
              
              {/* 404 page */}
              <Route path="*" element={<><Navbar /><NotFound /><Footer /></>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
